import { z } from "zod"

import { PricingPath } from "./types"

import { FreightTerms, Incoterms } from "@/graphql/codegen/graphql"

const tierFormSchema = z.object({
  qty: z.number().nullable(),
  uom: z.string().min(1, "UoM is required"),
  price: z.string().refine((value) => value === "" || Number(value) > 0, "Price must be a positive number"),
})

export const listPricingFormSchema = z.object({
  path: z.literal(PricingPath.List),
  shipFromLocationId: z.string().min(1, "Ship From is required"),
  incoterms: z.string().min(1, "Incoterms is required"),
  incotermsLocationId: z.string().min(1, "Incoterms Location is required"),
  estimatedAnnualVolume: z.number().nullable(),
  includeLinkedAccounts: z.boolean(),
})

export const specialPricingFormSchema = z.object({
  path: z.literal(PricingPath.Special),
  estimatedAnnualVolume: z.number().nullable(),
  pricePoint: z.string(),
  incoterms: z.array(z.nativeEnum(Incoterms)).min(1, "At least one Incoterm is required"),
  incotermsLocationId: z.string().min(1, "Incoterms Location is required"),
  shipFromLocationIds: z.array(z.string()).min(1, "At least one Ship From Location is required"),
  freightTerms: z.nativeEnum(FreightTerms, { required_error: "Freight Terms is required" }),
  includeLinkedAccounts: z.boolean(),
  effectiveDate: z.string(),
  validUntilDate: z.string().nullable(),
  tiers: z.array(tierFormSchema),
})

export const pricingFormSchema = z
  .discriminatedUnion("path", [listPricingFormSchema, specialPricingFormSchema])
  .superRefine((values, ctx) => {
    if (values.path !== PricingPath.Special) return
    if (!values.effectiveDate || !values.validUntilDate) return
    if (values.validUntilDate <= values.effectiveDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Valid Until Date must be after Effective Date",
        path: ["validUntilDate"],
      })
    }
  })

export type ListPricingFormValues = z.infer<typeof listPricingFormSchema>
export type SpecialPricingFormValues = z.infer<typeof specialPricingFormSchema>
export type PricingFormValues = z.infer<typeof pricingFormSchema>
export type PricingTierFormValue = z.infer<typeof tierFormSchema>
